import { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { CMatic, C_HEF, C_HOOGTE } from './trucks/CMatic'
import { PalletMetLading } from './PalletMetLading'
import { Contactschaduw } from './trucks/Contactschaduw'
import { A, B, F, RONDGANG, UIT, pendelStand } from './trucks/pendel'
import { OMLOOP, halklok } from './omloop'
import { STELLING, useIndeling } from './indeling'
import type { Indeling } from './indeling'
import { useAnimatie, useMaterialen } from '../scene/store'
import { PROEF } from './proef'
import { LindeBeeld, Volgorde, useLindeBeeld } from './trucks/LindeBeeld'
import { LINDE_BEELD } from './trucks/lindeKaarten'

/** Rondgangen van de pendel per omloop van de hal: zoveel verschillende pallets. */
const RONDEN = Math.round(OMLOOP / RONDGANG)

/**
 * Palletpendel: een C-MATIC die pallets tussen twee overslagstations rijdt.
 *
 * ## Waar en waarom daar
 *
 * Door de dwarsgang (`Indeling.dwarsgang`): een vak dat in elke stelling
 * openblijft, zodat hij loodrecht op de gangpaden de hele hal kan kruisen.
 * De bemande heftruck die hier eerder reed, ging op dezelfde x dwars door de
 * stellingen heen — die liepen toen zonder onderbreking door. Het hoofdscherm
 * kijkt door gangpad 04; daarin zie je de C-MATIC met zijn pallet oversteken.
 * De stations staan net buiten de stellingblokken, aan beide kanten.
 *
 * ## Wat hij doet
 *
 * Zoals op het typeblad: pallets direct op de adapterplaat, tussen
 * overslagstations. Een station is een stellage met twee rails op 290 mm; de
 * C-MATIC (260 mm) rijdt eronder, tilt 60 mm en neemt de pallet mee. Eén
 * rondgang:
 *
 *   A  optillen → naar B → afzetten → doorrijden, keren →
 *      leeg terug, onder beide stations door → voorbij A keren → onder A.
 *
 * Elke rit is een andere pallet — dozen, een gewikkelde lading, een kist, een
 * big bag, vaten — en dat is het enige kunstgreepje: de pallet op B verdwijnt
 * en de volgende op A verschijnt terwijl de C-MATIC aan de andere kant van
 * de hal is, 33 m verderop; de stations staan achter de stellingblokken en
 * zijn vanaf het hoofdscherm niet te zien.
 *
 * ## Tempo
 *
 * Het typeblad zegt 4,3 km/h beladen en 5,4 leeg. Dit liep eerst een kwart
 * sneller, in 57 s per rondgang; sinds 21 oktober rijdt hij op het typeblad,
 * met het gedeelde rijprofiel (`trucks/beweging.ts`), in ±81 s. Hij steekt
 * vanaf het hoofdscherm nog steeds elke anderhalve minuut gangpad 04 over.
 */

/** Bovenkant van de stationsrails: de "m2" van een C-MATIC-tafel. */
const RAIL_H = 0.29

// De stations (A, B), de keerpunten en de tijdlijn van een rondgang staan in
// `trucks/pendel.ts`, los van three.js zodat ze te toetsen zijn.

/* ------------------------------------------------------------------ stations en vloer */

/** QR-code zoals de C-MATIC ze op de vloer leest: drie vinders en ruis. */
function qrTextuur() {
  const n = 21
  const c = document.createElement('canvas')
  c.width = c.height = n * 4
  const g = c.getContext('2d')
  if (!g) return null
  g.fillStyle = '#f4f4f0'
  g.fillRect(0, 0, c.width, c.height)
  g.fillStyle = '#111'
  let s = 7
  const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647)
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (rnd() > 0.55) g.fillRect(x * 4, y * 4, 4, 4)
  for (const [x, y] of [[0, 0], [n - 7, 0], [0, n - 7]]) {
    g.fillStyle = '#f4f4f0'; g.fillRect((x - 1) * 4, (y - 1) * 4, 36, 36)
    g.fillStyle = '#111'; g.fillRect(x * 4, y * 4, 28, 28)
    g.fillStyle = '#f4f4f0'; g.fillRect(x * 4 + 4, y * 4 + 4, 20, 20)
    g.fillStyle = '#111'; g.fillRect(x * 4 + 8, y * 4 + 8, 12, 12)
  }
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  t.magFilter = THREE.NearestFilter
  return t
}

/** Overslagstation: twee rails op poten, met gele vloermarkering eromheen. */
function Station({ x, z }: { x: number; z: number }) {
  return (
    <group position={[x, 0, z]}>
      {[-1, 1].map((k) => (
        <group key={k}>
          <mesh position={[k * 0.5, RAIL_H - 0.025, 0]} castShadow receiveShadow>
            <boxGeometry args={[0.06, 0.05, 0.95]} />
            <meshStandardMaterial color="#5d636b" roughness={0.45} metalness={0.6} />
          </mesh>
          {[-0.42, 0.42].map((dz) => (
            <group key={dz}>
              <mesh position={[k * 0.5, (RAIL_H - 0.05) / 2, dz]} castShadow>
                <boxGeometry args={[0.05, RAIL_H - 0.05, 0.05]} />
                <meshStandardMaterial color="#f2c200" roughness={0.5} metalness={0.2} />
              </mesh>
              <mesh position={[k * 0.5, 0.004, dz]}>
                <boxGeometry args={[0.12, 0.008, 0.12]} />
                <meshStandardMaterial color="#4a4f56" roughness={0.6} metalness={0.5} />
              </mesh>
            </group>
          ))}
        </group>
      ))}
      {/* Vloermarkering: een geel kader rond het station. */}
      {([[0, 0.72, 1.5, 0.06], [0, -0.72, 1.5, 0.06], [0.72, 0, 0.06, 1.5], [-0.72, 0, 0.06, 1.5]] as const).map(
        ([x, dz, b, d], i) => (
          <mesh key={i} position={[x, 0.0025, dz]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[b, d]} />
            <meshStandardMaterial color="#e6b400" roughness={0.7} polygonOffset polygonOffsetFactor={-2} />
          </mesh>
        ),
      )}
    </group>
  )
}

/** QR-codes op de vloer langs de route, om de 1,4 m (de standaard-CUD). */
function QrCodes({ x }: { x: number }) {
  const tex = useMemo(() => qrTextuur(), [])
  useEffect(() => () => { tex?.dispose() }, [tex])
  const ref = useRef<THREE.InstancedMesh>(null)
  const zs = useMemo(() => {
    const uit: number[] = []
    for (let z = A - UIT; z <= B + UIT + 0.01; z += 1.4) uit.push(z)
    return uit
  }, [])
  useEffect(() => {
    const m = ref.current
    if (!m) return
    const d = new THREE.Object3D()
    zs.forEach((z, i) => {
      d.position.set(x, 0.003, z)
      d.rotation.set(-Math.PI / 2, 0, 0)
      d.updateMatrix()
      m.setMatrixAt(i, d.matrix)
    })
    m.instanceMatrix.needsUpdate = true
  }, [zs, x])
  if (!tex) return null
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, zs.length]} receiveShadow>
      <planeGeometry args={[0.1, 0.1]} />
      <meshStandardMaterial map={tex} roughness={0.8} polygonOffset polygonOffsetFactor={-2} />
    </instancedMesh>
  )
}

/* ------------------------------------------------------------------ de pendel */

/**
 * De x van de route: midden in de dwarsgang, of — in een hal met te weinig
 * vakken voor een dwarsgang — langs de gangpadkoppen.
 */
export function pendelX(ind: Indeling) {
  return ind.dwarsgang !== null
    ? ind.bayX(ind.dwarsgang)
    : ind.bayX(ind.cfg.bays - 1) + STELLING.bayBreedte / 2 + 2.5
}

export function Palletpendel() {
  const ind = useIndeling()
  const X = pendelX(ind)
  const anim = useAnimatie()
  const mat = useMaterialen()
  const kleuren = { rood: mat.truckRood, zwart: mat.truckZwart }

  const robot = useRef<THREE.Group>(null)
  const model = useRef<THREE.Group>(null)
  const linde = useLindeBeeld()
  const plaat = useRef<THREE.Group | null>(null)
  const mee = useRef<THREE.Group>(null)
  const opA = useRef<THREE.Group>(null)
  const opB = useRef<THREE.Group>(null)
  // De rondgang telt: elke rondgang een nieuwe pallet. Alleen bij een nieuwe
  // rondgang een rerender, niet per frame.
  const [ronde, zetRonde] = useState(0)

  // De klok van de hal, zoals de Matics (`omloop.ts`). De rondgang telt rond
  // binnen de omloop: drie pallets, steeds dezelfde drie, anders klopt de
  // pallet in de film niet met die in de hal.
  const klok = useRef(0)

  useFrame(() => {
    if (PROEF.pendel !== null) klok.current = PROEF.pendel * RONDGANG
    else if (anim.aan && anim.heftruck) klok.current = halklok.t
    const r = klok.current / RONDGANG
    const n = Math.floor(r) % RONDEN
    const t = r - Math.floor(r)
    // Tot React de nieuwe rondgang heeft getekend, dragen de pallets nog de
    // lading van de vorige. Dat is precies op het moment dat de C-MATIC
    // onder A de volgende pallet optilt — die van A, dus die laten we staan.
    const achter = n !== ronde
    if (achter) zetRonde(n)

    const p = pendelStand(t)
    const hef = p.hef * C_HEF
    if (robot.current) {
      robot.current.position.set(X, 0, p.z)
      robot.current.rotation.y = p.yaw
    }
    if (plaat.current) plaat.current.position.y = hef
    // De pallet ligt op de plaat, maar nooit lager dan de rails van een
    // station: optillen begint pas als de plaat de pallet raakt.
    if (mee.current) {
      mee.current.visible = p.geladen && !achter
      const opStation = Math.abs(p.z - A) < 0.05 || Math.abs(p.z - B) < 0.05
      mee.current.position.set(X, opStation ? Math.max(RAIL_H, C_HOOGTE + hef) : C_HOOGTE + hef, p.z)
    }
    // Pallet op A: de volgende, zodra de C-MATIC bij B is. Pallet op B: deze,
    // vanaf het afzetten tot hij aan de andere kant keert.
    if (opA.current) opA.current.visible = t > F[2] + 0.03 || achter
    if (opB.current) opB.current.visible = t >= F[2] && t < F[5] + 0.02
  })

  return (
    <group>
      <Station x={X} z={A} />
      <Station x={X} z={B} />
      <QrCodes x={X} />
      <group ref={robot} name="C-Matic" position={[X, 0, A]}>
        <group ref={model}><CMatic kleuren={kleuren} hef={plaat} /></group>
        <Contactschaduw breedte={0.83} lengte={1.18} />
      </group>
      {linde && <LindeBeeld bron={LINDE_BEELD['C-Matic']} drager={robot} model={model} />}
      {/* Pallets na het Linde-beeld (`LindeBeeld`): ze liggen erop en ervoor. */}
      <Volgorde n={linde ? 11 : 0}>
        <group ref={mee}><PalletMetLading n={ronde} /></group>
        <group ref={opA} position={[X, RAIL_H, A]} visible={false}><PalletMetLading n={(ronde + 1) % RONDEN} /></group>
        <group ref={opB} position={[X, RAIL_H, B]} visible={false}><PalletMetLading n={ronde} /></group>
      </Volgorde>
    </group>
  )
}
